"use client";

import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { ComponentPropsWithoutRef, FC, useState } from "react";
import { useIsTouchDevice } from "../hooks/useIsTouchDevice";
import {
  SubscriptionPlan,
  usePlanCheckout,
} from "../hooks/usePlanCheckout";
import { PricingExperimentVariant } from "../hooks/usePricingExperiment";
import Button from "./Button";
import Typography from "./Typography";

type PricingCardProps = {
  price: number;
  features: Array<{
    text: string;
    tooltip?: string;
  }>;
  /** Stage/outcome descriptor above the price. */
  eyebrow: string;
  /** Muted contract line under the price (quota). */
  specText: string;
  specTooltip?: string;
  plan: SubscriptionPlan;
  experimentVariant?: PricingExperimentVariant;
} & ComponentPropsWithoutRef<"section">;

const FeatureRow = ({
  feature,
}: {
  feature: { text: string; tooltip?: string };
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isTouchDevice = useIsTouchDevice();

  const showDropdown = isTouchDevice;
  const showTooltip = !isTouchDevice;

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center gap-2 w-full">
        <CheckCircleIcon className="w-5 h-5 shrink-0" />
        <Typography variant="caption">{feature.text}</Typography>
        {showDropdown && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="ml-auto focus:outline-none"
          >
            {isOpen ? (
              <ChevronUpIcon className="w-4 h-4 text-slate-500" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-slate-500" />
            )}
          </button>
        )}
        {showTooltip && (
          <InformationCircleIcon
            data-tooltip-id="global"
            data-tooltip-content={feature.tooltip}
            className="w-4 h-4 shrink-0 text-slate-500 ml-auto cursor-pointer"
          />
        )}
      </div>
      {isOpen && showDropdown && (
        <div className="flex flex-row items-center gap-2">
          <InformationCircleIcon className="w-4 h-4 shrink-0 text-slate-500 ml-auto cursor-pointer" />
          <div className="mt-2 text-sm text-slate-400 p-2 transition-all duration-200 ease-in-out text-left">
            {feature.tooltip}
          </div>
        </div>
      )}
    </div>
  );
};

export const getPlanName = (plan: SubscriptionPlan) => {
  if (plan.includes("plus")) return "Pro+";
  if (plan.includes("pro")) return "Pro";
  return "Basic";
};

const getTierKey = (plan: SubscriptionPlan) => {
  if (plan.includes("plus")) return "pro-plus" as const;
  if (plan.includes("pro")) return "pro" as const;
  return "basic" as const;
};

const TIER_STYLES = {
  basic: {
    card: "bg-white border border-slate-200",
    cta: "secondary" as const,
  },
  pro: {
    card: "bg-slate-150",
    cta: "primary" as const,
  },
  "pro-plus": {
    card: "bg-white border border-slate-200",
    cta: "secondary" as const,
  },
};

const PricingCard: FC<PricingCardProps> = ({
  features,
  eyebrow,
  specText,
  specTooltip,
  price,
  plan,
  experimentVariant,
  className,
  ...rest
}) => {
  const { handleCheckout, ctaLabel, isLoading, isCurrentPlan } =
    usePlanCheckout({ plan, experimentVariant });

  const tierKey = getTierKey(plan);
  const styles = TIER_STYLES[tierKey];
  const isYearly = plan.includes("yearly");
  const moIndex = specText.lastIndexOf("/mo");
  const specEmphasis =
    moIndex >= 0 ? specText.slice(0, moIndex) : specText;
  const specMuted = moIndex >= 0 ? specText.slice(moIndex) : null;

  return (
    <section
      {...rest}
      className={`relative overflow-hidden flex-1 flex flex-col p-6 rounded-3xl text-left ${styles.card} ${className ?? ""}`}
    >
      <div className="flex items-center gap-2">
        <Typography variant="h4" component="h3">
          {getPlanName(plan)}
        </Typography>
        {tierKey === "pro" && !isCurrentPlan ? (
          <Typography
            variant="overline"
            component="span"
            className="rounded-full px-2.5 py-1 font-medium bg-blue-500 text-white whitespace-nowrap"
          >
            Popular
          </Typography>
        ) : null}
      </div>

      <Typography className="mt-3 text-slate-500" variant="body2">
        {eyebrow}
      </Typography>

      <Typography
        variant="body2"
        component="p"
        className="mt-0.5 text-slate-500 inline-flex items-center gap-1 flex-wrap"
      >
        <span>
          <span className="text-slate-900">{specEmphasis}</span>
          {specMuted}
        </span>
        {specTooltip ? (
          <InformationCircleIcon
            data-tooltip-id="global"
            data-tooltip-html={specTooltip}
            className="w-4 h-4 shrink-0 text-slate-400 cursor-pointer"
          />
        ) : null}
      </Typography>

      <Typography
        variant="h3"
        component="p"
        className="mt-6 flex items-baseline gap-0.5"
      >
        <span>{"$" + price}</span>
        <Typography
          variant="body2"
          component="span"
          className="text-slate-500 !font-normal"
        >
          /mo
        </Typography>
      </Typography>

      {isYearly ? (
        <Typography
          variant="overline"
          component="p"
          className="mt-1 text-slate-500"
        >
          Billed yearly
        </Typography>
      ) : null}

      <Button
        variant={styles.cta}
        className="mt-6 w-full"
        disabled={isCurrentPlan}
        onClick={handleCheckout}
        size="l"
        loading={isLoading}
      >
        {isCurrentPlan ? (
          <CheckCircleIcon className="w-5 h-5 shrink-0" />
        ) : null}
        {ctaLabel}
      </Button>

      {features.length > 0 ? (
        <ul className="flex-1 self-start flex flex-col mt-8 gap-4 w-full">
          {features.map((feature) => (
            <li
              key={feature.text}
              className="flex gap-2 items-center text-left"
            >
              <FeatureRow feature={feature} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default PricingCard;
